import { Link } from "react-router-dom";
import { useState } from "react";
import {
  ArrowUpRight,
  ShieldCheck,
  FileCheck2,
  Handshake,
  Ship,
  Wheat,
  Apple,
  Recycle,
  FlaskConical,
  Cog,
  Gem,
  Users,
  Globe2,
  Network,
  Linkedin,
  Building2,
  TrendingDown,
  FileSearch,
  Award,
  ClipboardCheck,
  Eye,
  Truck,
  BookOpen,
  TrendingUp,
  Factory,
  Leaf,
  Scale,
  PiggyBank,
  ArrowRight,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

// ---- DATA ----
const TRUST = [
  { label: "Supplier Verification", icon: ShieldCheck },
  { label: "Documentation Support", icon: FileCheck2 },
  { label: "Price Negotiation", icon: Handshake },
  { label: "Export Coordination", icon: Ship },
];

const TRADE_FLOW = [
  { city: "Mumbai", coord: "INDIA · WEST" },
  { city: "Chennai", coord: "INDIA · SOUTH" },
  { city: "London", coord: "UK" },
  { city: "Rotterdam", coord: "NL · EU GATEWAY" },
  { city: "Hamburg", coord: "DE" },
];

const WHY_EUROPE = [
  { t: "Competitive costs", icon: PiggyBank },
  { t: "High quality", icon: Award },
  { t: "Compliance growing", icon: ShieldCheck },
  { t: "Agricultural scale", icon: Wheat },
  { t: "Sustainable packaging", icon: Leaf },
  { t: "Engineering depth", icon: Cog },
  { t: "EU / UK ties", icon: Globe2 },
];

// Illustrative trade volume index (relative)
const TRADE_CHART = [
  { sector: "Agri", value: 78 },
  { sector: "Packaging", value: 64 },
  { sector: "Botanicals", value: 52 },
  { sector: "Industrial", value: 86 },
  { sector: "Metals", value: 41 },
  { sector: "Fresh Produce", value: 58 },
];

// Image-led category cards (Unsplash royalty-free)
const EXPORT_CATEGORIES = [
  {
    slug: "agricultural-commodities",
    icon: Wheat,
    name: "Agricultural Commodities",
    bullets: ["Millets", "Grains", "Sesame", "Spices", "Pulses"],
    image:
      "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?auto=format&fit=crop&w=1200&q=70",
  },
  {
    slug: "fresh-produce",
    icon: Apple,
    name: "Fresh Produce",
    bullets: ["Mangoes", "Pomegranates", "Bananas", "Seasonal"],
    image:
      "https://images.unsplash.com/photo-1553279768-865429fa0078?auto=format&fit=crop&w=1200&q=70",
  },
  {
    slug: "sustainable-packaging",
    icon: Recycle,
    name: "Sustainable Packaging",
    bullets: ["Jute", "Eco", "Retail", "Corporate"],
    image:
      "https://images.unsplash.com/photo-1607344645866-009c320b63e0?auto=format&fit=crop&w=1200&q=70",
  },
  {
    slug: "essential-oils-botanicals",
    icon: FlaskConical,
    name: "Essential Oils & Botanicals",
    bullets: ["Turmeric", "Herbal", "Essential Oils", "Natural"],
    image: null,
  },
  {
    slug: "industrial-engineering",
    icon: Cog,
    name: "Industrial & Engineering",
    bullets: ["Steel", "Fabrication", "Machinery", "Equipment"],
    image: null,
  },
  {
    slug: "precious-metals-custom",
    icon: Gem,
    name: "Precious Metals & Custom",
    bullets: ["Gold", "Silver", "Custom Designs"],
    image:
      "https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=1200&q=70",
  },
];

const STEPS = [
  { n: "01", t: "Brief", icon: FileSearch },
  { n: "02", t: "Match", icon: Network },
  { n: "03", t: "Verify", icon: ShieldCheck },
  { n: "04", t: "Sample", icon: FlaskConical },
  { n: "05", t: "Produce", icon: Factory },
  { n: "06", t: "Ship", icon: Ship },
];

const COMPLIANCE_BADGES = [
  { t: "Supplier Verification", icon: ShieldCheck },
  { t: "Origin Certificates", icon: Award },
  { t: "Export Docs", icon: FileCheck2 },
  { t: "Quality Inspection", icon: ClipboardCheck },
  { t: "Traceability", icon: Eye },
  { t: "Logistics", icon: Truck },
  { t: "Regulatory", icon: BookOpen },
];

const WHY_CHOOSE = [
  { icon: Network, t: "Direct Access" },
  { icon: TrendingDown, t: "Better Pricing" },
  { icon: Handshake, t: "Trusted Relationships" },
  { icon: FileSearch, t: "Faster Procurement" },
  { icon: Globe2, t: "Global Perspective" },
  { icon: Users, t: "Real People" },
];

const KPIS = [
  { v: "60+", l: "Years combined experience" },
  { v: "200+", l: "Indian export destinations" },
  { v: "6", l: "Priority verticals" },
  { v: "24h", l: "First-response window" },
];

const BAR_COLORS = ["#E8B86A", "#D6A04A", "#E8B86A", "#D6A04A", "#E8B86A", "#D6A04A"];

// ---- COMPONENT ----
export default function Home() {
  return (
    <div className="bg-zinc-950">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-grid opacity-[0.5] pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-zinc-950/60 to-zinc-950 pointer-events-none" />
        <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="absolute -top-10 right-0 h-[420px] w-[420px] rounded-full bg-amber-400/[0.08] blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-16 sm:pt-20 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="flex items-center gap-2 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse-dot" />
              <span className="font-mono text-[11px] tracking-[0.22em] text-zinc-400">
                INDIA → UK · EUROPE · GLOBAL
              </span>
            </div>
            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="hero-title">
              India&apos;s Direct Gateway to <span className="text-amber-400">UK & European</span> Supply Chains
            </h1>
            <p className="mt-5 text-zinc-300 text-base sm:text-lg leading-relaxed font-medium">
              Verified manufacturers · Direct sourcing · Compliance support
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/quote" className="inline-flex items-center gap-1.5 text-sm font-medium px-5 py-3 rounded-md bg-amber-400 text-zinc-950 hover:bg-amber-300 transition-colors" data-testid="hero-cta-quote">
                Get a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/become-a-supplier" className="inline-flex items-center gap-1.5 text-sm font-medium px-5 py-3 rounded-md bg-blue-600 hover:bg-blue-500 text-white transition-colors" data-testid="hero-cta-supplier">
                Become a Supplier <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Trade-flow visualization */}
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "180ms" }}>
            <TradeFlowGraphic />
          </div>
        </div>

        {/* TRUST STRIP */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pb-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 border border-white/5 rounded-lg overflow-hidden bg-zinc-900/40 backdrop-blur-sm" data-testid="trust-bar">
            {TRUST.map((t, i) => (
              <div key={t.label} className={`p-5 flex items-center gap-3 ${i < TRUST.length - 1 ? "lg:border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} border-white/5`}>
                <t.icon className="h-4 w-4 text-amber-400" />
                <div className="text-[13px] text-zinc-200">{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KPI STRIP */}
      <section className="border-y border-white/5 bg-zinc-950" data-testid="kpi-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
          {KPIS.map((k) => (
            <div key={k.l} className="bg-zinc-950 px-5 py-7">
              <div className="font-display text-4xl font-bold tracking-tight text-white tnum">{k.v}</div>
              <div className="mt-1 text-[12px] text-zinc-500">{k.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY EUROPE — visual tiles + chart */}
      <section className="bg-zinc-900/30 border-b border-white/5" data-testid="why-europe">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="WHY EUROPE BUYS FROM INDIA"
              title="Seven reasons. One partner."
            />
            <div className="mt-6 grid grid-cols-2 gap-2.5">
              {WHY_EUROPE.map((b, i) => (
                <div key={b.t} className="flex items-center gap-2.5 border border-white/5 bg-zinc-950 rounded-md px-3.5 py-3" data-testid={`why-europe-${i}`}>
                  <b.icon className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  <span className="text-[13px] text-zinc-200">{b.t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-white/5 bg-zinc-950 rounded-lg p-5 sm:p-6" data-testid="trade-chart-wrap">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.22em] text-amber-400">INDIA EXPORT INDEX</div>
                  <div className="mt-1 text-[13px] text-zinc-400">Illustrative demand strength across our verticals</div>
                </div>
                <TrendingUp className="h-4 w-4 text-amber-400" />
              </div>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={TRADE_CHART} margin={{ top: 4, right: 6, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="sector" tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#52525b", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      cursor={{ fill: "rgba(255,255,255,0.04)" }}
                      contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: "#e4e4e7", fontWeight: 600 }}
                      itemStyle={{ color: "#E8B86A" }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {TRADE_CHART.map((_, idx) => (
                        <Cell key={idx} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 text-[11px] text-zinc-500">Index values are illustrative; actual demand varies by buyer brief.</div>
            </div>
          </div>
        </div>
      </section>

      {/* PRIORITY EXPORT CATEGORIES — image-led */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20" data-testid="export-categories">
        <SectionHeading
          eyebrow="PRIORITY EXPORT CATEGORIES"
          title="Six verticals. Verified at the source."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
          {EXPORT_CATEGORIES.map((c, i) => (
            <CategoryCard key={c.slug} c={c} i={i} />
          ))}
        </div>
      </section>

      <div className="gold-divider max-w-7xl mx-auto" />

      {/* HOW IT WORKS — visual stepper */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20" data-testid="home-how-it-works">
        <SectionHeading eyebrow="HOW VYR TRADE WORKS" title="Six steps. One contact." />
        <div className="mt-12">
          <div className="hidden md:flex items-center justify-between gap-2">
            {STEPS.map((s, i) => (
              <div key={s.n} className="flex items-center" data-testid={`step-${s.n}`}>
                <div className="flex flex-col items-center text-center w-[120px]">
                  <div className="h-14 w-14 rounded-full border border-amber-400/30 bg-gradient-to-br from-amber-400/15 to-transparent grid place-items-center">
                    <s.icon className="h-5 w-5 text-amber-300" />
                  </div>
                  <div className="mt-3 font-mono text-[10px] tracking-[0.22em] text-zinc-500">{s.n}</div>
                  <div className="mt-1 font-display text-[14px] font-semibold text-zinc-100">{s.t}</div>
                </div>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="h-4 w-4 text-zinc-700 mx-1" />
                )}
              </div>
            ))}
          </div>
          {/* Mobile stack */}
          <div className="md:hidden grid grid-cols-2 gap-2">
            {STEPS.map((s) => (
              <div key={s.n} className="border border-white/5 bg-zinc-950 rounded-lg p-4 flex flex-col items-center text-center">
                <s.icon className="h-5 w-5 text-amber-300" />
                <div className="mt-2 font-mono text-[10px] text-zinc-500">{s.n}</div>
                <div className="mt-1 font-display text-[13px] font-semibold">{s.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VYR ADVANTAGE — visual statement */}
      <section className="bg-zinc-900/30 border-y border-white/5" data-testid="vyr-advantage">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] tracking-[0.22em] text-amber-400">THE VYR ADVANTAGE</div>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight">Not faceless brokers.</h2>
            <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight text-amber-400">Real business operators.</h2>
            <p className="mt-4 text-amber-300/90 text-[14px] italic">If we cannot create value, we do not deserve your business.</p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-3">
            {[
              { t: "No middlemen", icon: Network },
              { t: "No inflated pricing", icon: Scale },
              { t: "Transparent comms", icon: Eye },
              { t: "Long-term partners", icon: Handshake },
            ].map((p) => (
              <div key={p.t} className="border border-white/5 bg-zinc-950 rounded-md p-5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-md border border-amber-400/20 grid place-items-center bg-amber-400/[0.05] shrink-0">
                  <p.icon className="h-4 w-4 text-amber-400" />
                </div>
                <div className="text-[14px] font-medium text-zinc-100 leading-snug">{p.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE — icon grid only */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20" data-testid="compliance">
        <SectionHeading eyebrow="COMPLIANCE & QUALITY" title="Built for international trade." />
        <div className="mt-10 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="compliance-badges">
          {COMPLIANCE_BADGES.map((b) => (
            <div key={b.t} className="border border-white/5 bg-zinc-900/40 hover:border-amber-400/30 rounded-lg p-4 flex flex-col items-center text-center transition-colors">
              <div className="h-10 w-10 rounded-md border border-amber-400/20 grid place-items-center bg-gradient-to-br from-amber-400/10 to-transparent">
                <b.icon className="h-4 w-4 text-amber-400" />
              </div>
              <div className="mt-3 text-[11.5px] font-medium text-zinc-200 leading-tight">{b.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE — minimal icon tiles */}
      <section className="bg-zinc-900/30 border-y border-white/5" data-testid="why-choose">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading eyebrow="WHY CHOOSE VYR TRADE" title="Six reasons. No filler." />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3">
            {WHY_CHOOSE.map((p, i) => (
              <div key={p.t} className="border border-white/5 bg-zinc-950 rounded-lg p-6 flex flex-col items-start gap-4 hover:border-amber-400/30 transition-colors" data-testid={`why-choose-${i}`}>
                <div className="h-11 w-11 rounded-md border border-amber-400/20 grid place-items-center bg-gradient-to-br from-amber-400/10 to-transparent">
                  <p.icon className="h-5 w-5 text-amber-400" />
                </div>
                <div className="font-display text-[17px] font-semibold leading-tight">{p.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20" data-testid="founders">
        <SectionHeading eyebrow="FOUNDERS" title="Meet the founders." />
        <div className="mt-10 grid md:grid-cols-2 gap-3">
          <FounderCard name="Umesh Sridhar" role="Co-Founder" initials="US" location="London, UK" />
          <FounderCard name="Adithya Menon" role="Co-Founder" initials="AM" location="Hyderabad, India" />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20" data-testid="home-cta">
        <div className="relative overflow-hidden border border-white/10 rounded-xl bg-gradient-to-br from-zinc-900 via-zinc-950 to-zinc-900 p-8 sm:p-12">
          <div className="absolute inset-0 bg-dots opacity-40 pointer-events-none" />
          <div className="relative grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <div className="font-mono text-[11px] tracking-[0.22em] text-amber-400">START NOW</div>
              <h3 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight">
                Direct. <span className="text-amber-400">Transparent.</span> Reliable.
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
              <Link to="/quote" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-amber-400 text-zinc-950 hover:bg-amber-300 font-semibold text-sm transition-colors" data-testid="home-cta-quote">
                Get a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/become-a-supplier" className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/15 hover:border-white/30 hover:bg-white/5 text-white font-medium text-sm transition-colors" data-testid="home-cta-supplier">
                Become a Supplier <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---- HELPERS ----
function CategoryCard({ c, i }) {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <Link
      to="/quote"
      className="group relative overflow-hidden rounded-lg border border-white/5 hover:border-amber-400/30 transition-colors"
      data-testid={`export-cat-${c.slug}`}
    >
      <div className="relative aspect-[16/11] overflow-hidden bg-zinc-900">
        {(!c.image || imgFailed) ? (
          <div className="h-full w-full bg-gradient-to-br from-blue-950 via-zinc-950 to-amber-500/15 grid place-items-center relative">
            <div className="absolute inset-0 bg-dots opacity-30" />
            <c.icon className="relative h-16 w-16 text-amber-400/60" strokeWidth={1.3} />
          </div>
        ) : (
          <img
            src={c.image}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-[1.04] transition-all duration-500 grayscale-[40%] contrast-110"
          />
        )}
        {/* Duotone navy → amber tint, keeps cards cohesive regardless of photo subject */}
        {c.image && !imgFailed && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/40 via-zinc-950/30 to-amber-500/25 mix-blend-multiply pointer-events-none" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/65 to-zinc-950/20 pointer-events-none" />
        <div className="absolute top-3 left-3 flex items-center gap-2 bg-zinc-950/70 backdrop-blur-sm border border-white/10 rounded-md px-2 py-1">
          <c.icon className="h-3.5 w-3.5 text-amber-400" />
          <span className="font-mono text-[10px] tracking-[0.22em] text-zinc-200 uppercase">{c.bullets[0]} · {c.bullets[1] || ""}</span>
        </div>
        <div className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.22em] text-zinc-300 bg-zinc-950/70 backdrop-blur-sm border border-white/10 rounded-md px-2 py-1">
          EXP.{String(i + 1).padStart(2, "0")}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="font-display text-[17px] font-semibold text-white leading-tight">{c.name}</div>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {c.bullets.map((b) => (
              <span key={b} className="text-[10.5px] font-medium text-amber-200/95 bg-amber-400/[0.08] border border-amber-400/15 rounded-full px-2 py-0.5">{b}</span>
            ))}
          </div>
        </div>
        <div className="absolute bottom-3 right-3 h-7 w-7 rounded-full bg-amber-400 text-zinc-950 grid place-items-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
          <ArrowUpRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  );
}

function SectionHeading({ eyebrow, title, sub = "" }) {
  return (
    <div className="max-w-3xl">
      <div className="font-mono text-[11px] tracking-[0.22em] text-amber-400">{eyebrow}</div>
      <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight">{title}</h2>
      {sub ? <p className="mt-3 text-zinc-400 text-[15px] leading-relaxed">{sub}</p> : null}
    </div>
  );
}

function FounderCard({ name, role, location, initials }) {
  return (
    <div className="border border-white/5 bg-zinc-900/40 rounded-lg p-6 flex gap-5 items-start" data-testid={`founder-${initials.toLowerCase()}`}>
      <div className="h-16 w-16 rounded-md bg-gradient-to-br from-blue-600/40 to-amber-400/20 grid place-items-center border border-white/10 shrink-0">
        <Building2 className="h-6 w-6 text-amber-300/80" />
      </div>
      <div className="flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display font-semibold text-lg">{name}</div>
            <div className="font-mono text-[12px] text-zinc-400 tracking-wide">{role}</div>
            <div className="text-[12px] text-amber-400/90 mt-1">{location}</div>
          </div>
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="h-8 w-8 rounded-md border border-white/10 grid place-items-center text-zinc-500 hover:text-amber-300 hover:border-amber-400/40 transition-colors"
            aria-label={`LinkedIn — ${name} (coming soon)`}
            title="LinkedIn profile coming soon"
            data-testid={`founder-${initials.toLowerCase()}-linkedin`}
          >
            <Linkedin className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="mt-4 inline-flex items-center gap-2 border border-white/5 bg-zinc-950 rounded-md px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse-dot" />
          <span className="font-mono text-[10.5px] tracking-[0.22em] text-zinc-300">PROFILE COMING SOON</span>
        </div>
      </div>
    </div>
  );
}

function TradeFlowGraphic() {
  return (
    <div className="relative border border-white/10 bg-zinc-950 rounded-xl p-5 sm:p-6 overflow-hidden" data-testid="trade-flow">
      <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-amber-400">LIVE TRADE ROUTES</div>
            <div className="mt-1 text-[12px] text-zinc-400">Sea & air corridors</div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
            <span className="font-mono tracking-wide">ACTIVE</span>
          </div>
        </div>

        {/* Two-column origin → destination */}
        <div className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-5 space-y-2">
            <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-2">ORIGIN</div>
            {TRADE_FLOW.slice(0, 2).map((p) => (
              <div key={p.city} className="flex items-center gap-2 border border-white/5 bg-zinc-900/40 rounded-md px-2.5 py-2">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                <div>
                  <div className="text-[12.5px] text-zinc-100 leading-none">{p.city}</div>
                  <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{p.coord}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="col-span-2 grid place-items-center">
            <FlowConnector />
          </div>
          <div className="col-span-5 space-y-2">
            <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-2 text-right">DESTINATION</div>
            {TRADE_FLOW.slice(2).map((p) => (
              <div key={p.city} className="flex items-center justify-end gap-2 border border-white/5 bg-zinc-900/40 rounded-md px-2.5 py-2">
                <div className="text-right">
                  <div className="text-[12.5px] text-zinc-100 leading-none">{p.city}</div>
                  <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{p.coord}</div>
                </div>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </div>
            ))}
          </div>
        </div>

        {/* Footer stats */}
        <div className="mt-5 grid grid-cols-3 gap-2 pt-4 border-t border-white/5">
          <Stat v="FCL/LCL" l="Multi-modal" />
          <Stat v="200+" l="Destinations" />
          <Stat v="24h" l="First reply" />
        </div>
      </div>
    </div>
  );
}

function FlowConnector() {
  return (
    <svg width="100%" height="120" viewBox="0 0 80 120" fill="none" className="text-amber-400/70">
      <defs>
        <linearGradient id="flowGrad" x1="0" y1="0" x2="80" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#E8B86A" stopOpacity="0" />
          <stop offset="50%" stopColor="#E8B86A" />
          <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M 4 30 Q 40 30, 76 60" stroke="url(#flowGrad)" strokeWidth="1.5" fill="none" />
      <path d="M 4 90 Q 40 90, 76 60" stroke="url(#flowGrad)" strokeWidth="1.5" fill="none" />
      <circle cx="40" cy="60" r="3" fill="#E8B86A" />
      <circle cx="40" cy="60" r="8" fill="#E8B86A" fillOpacity="0.15" />
      <text x="40" y="115" fill="currentColor" fontSize="9" fontFamily="monospace" textAnchor="middle" opacity="0.8">VYR</text>
    </svg>
  );
}

function Stat({ v, l }) {
  return (
    <div className="text-center">
      <div className="font-display font-bold text-[15px] text-white tnum">{v}</div>
      <div className="text-[10px] text-zinc-500 font-mono mt-0.5 tracking-wide">{l.toUpperCase()}</div>
    </div>
  );
}
